import type {ShellAccount} from '@wandb/forge-shell-contract';

import type {OrgType, ShellAccountDataQuery} from '../generated/graphql';

export enum AccountType {
  Personal = 'Personal',
  Corporate = 'Corporate',
  Academic = 'Academic',
}

export type Account = {
  name: string;
  accountType: AccountType;
  id: string;
  memberCount: number;
  username: string;
  personalOrgId?: string;
  isDummyAccount?: boolean;
};

export type ShellViewer = NonNullable<ShellAccountDataQuery['viewer']>;
export type ShellOrganization = ShellViewer['organizations'][number];
export type ShellSubscription = ShellOrganization['subscriptions'][number];

const PERSONAL_ORG_TYPE: OrgType = 'PERSONAL';
const ACADEMIC_SUBSCRIPTION_TYPES = new Set(['ACADEMIC', 'ACADEMIC_TRIAL']);
const PLAN_TYPE_PRIMARY = 'PRIMARY';

export function getPrimarySub(
  organization?: Pick<ShellOrganization, 'subscriptions'> | null
): ShellSubscription | null {
  return (
    organization?.subscriptions.find(
      subscription => subscription.plan.planType === PLAN_TYPE_PRIMARY
    ) ?? null
  );
}

export function getAccountType(
  organization: Pick<ShellOrganization, 'orgType' | 'subscriptions'>
): AccountType {
  if (organization.orgType === PERSONAL_ORG_TYPE) {
    return AccountType.Personal;
  }

  const primarySub = getPrimarySub(organization);
  if (primarySub == null) {
    return AccountType.Personal;
  }

  if (ACADEMIC_SUBSCRIPTION_TYPES.has(primarySub.subscriptionType)) {
    return AccountType.Academic;
  }

  return AccountType.Corporate;
}

function compareAccountNames(left: Account, right: Account): number {
  if (left.name < right.name) {
    return -1;
  }
  if (left.name > right.name) {
    return 1;
  }
  return 0;
}

export function buildAccountList(
  organizations: ShellOrganization[],
  viewer: ShellViewer,
  isPersonalEntityHidden: boolean,
  hasPersonalEntity: boolean
): Account[] {
  const accounts = organizations.map(organization => {
    const accountType = getAccountType(organization);
    return {
      name:
        accountType === AccountType.Personal
          ? (viewer.username ?? '')
          : organization.name,
      accountType,
      id: accountType === AccountType.Personal ? viewer.id : organization.id,
      personalOrgId:
        accountType === AccountType.Personal ? organization.id : undefined,
      memberCount: organization.memberCount,
      username: viewer.username ?? '',
    };
  });
  accounts.sort(compareAccountNames);

  if (
    isPersonalEntityHidden &&
    accounts.some(account => account.accountType !== AccountType.Personal)
  ) {
    return accounts.filter(
      account => account.accountType !== AccountType.Personal
    );
  }

  const personalAccount = accounts.find(
    account => account.accountType === AccountType.Personal
  );
  if (personalAccount == null) {
    if (hasPersonalEntity && !isPersonalEntityHidden) {
      return [
        {
          name: viewer.username ?? '',
          accountType: AccountType.Personal,
          id: viewer.id,
          memberCount: 1,
          username: viewer.username ?? '',
        },
        ...accounts,
      ];
    }
    return accounts;
  }

  return [
    personalAccount,
    ...accounts.filter(account => account.accountType !== AccountType.Personal),
  ];
}

// Maps a remote's URL account broadcast to a shell Account. Member broadcasts
// resolve against the viewer's account list; a broadcast that names nothing in
// the list resolves to undefined. Dummy broadcasts stand in for accounts the
// viewer can only see under admin privileges, so they never match the list.
export function accountFromUrlBroadcast(
  broadcast: ShellAccount,
  viewer: ShellViewer,
  accountList: Account[]
): Account | undefined {
  if (broadcast.isDummyAccount) {
    return {
      id: broadcast.id,
      name: broadcast.name,
      accountType:
        broadcast.kind === 'personal'
          ? AccountType.Personal
          : AccountType.Corporate,
      memberCount: -1,
      username: viewer.username ?? '',
      isDummyAccount: true,
    };
  }
  return (
    accountList.find(account => account.id === broadcast.id) ??
    accountList.find(
      account =>
        account.name === broadcast.name &&
        (broadcast.kind === 'personal'
          ? account.accountType === AccountType.Personal
          : account.accountType !== AccountType.Personal)
    )
  );
}

export function getDefaultAccount(
  viewer: ShellViewer,
  accountList: Account[]
): Account | null {
  const defaultEntityTeam = (viewer.teams?.edges ?? []).find(
    team => team.node?.id === viewer.defaultEntity?.id
  )?.node;
  const account = accountList.find(
    candidate => candidate.id === defaultEntityTeam?.organizationId
  );
  const isPersonalEntity =
    defaultEntityTeam?.name === viewer.username && defaultEntityTeam != null;
  const paidPersonalAccount =
    accountList.find(
      candidate =>
        candidate.accountType === AccountType.Personal &&
        candidate.personalOrgId != null
    ) ?? null;

  if (account != null) {
    return {
      ...account,
      accountType: isPersonalEntity
        ? AccountType.Personal
        : account.accountType,
    };
  }

  return paidPersonalAccount ?? accountList[0] ?? null;
}

export function isLegacyPersonalEntity(
  entity:
    | {
        entityType: string | null;
        isTeam: boolean;
      }
    | null
    | undefined
): boolean {
  return (
    entity?.entityType === 'PERSONAL' ||
    (entity?.entityType == null && entity?.isTeam === false)
  );
}

export function hasPersonalEntity(
  entity:
    | {
        entityType: string | null;
        isTeam: boolean;
      }
    | null
    | undefined
): boolean {
  return isLegacyPersonalEntity(entity);
}

export const ACCOUNT_SELECTOR_VALUE = 'account-selector-value';
export const LAST_ACCOUNT_SELECTOR_VALUE = 'last-account-selector-value';
export const LAST_NON_IMPERSONATED_ACCOUNT = 'last-non-impersonated-account';

export function safelyParseAccountJson(
  json: string,
  accountList: Account[]
): Account | null {
  try {
    const parsed: unknown = JSON.parse(json);
    if (
      typeof parsed !== 'object' ||
      parsed == null ||
      !('id' in parsed) ||
      typeof parsed.id !== 'string'
    ) {
      return null;
    }
    return accountList.find(account => account.id === parsed.id) ?? null;
  } catch {
    return null;
  }
}

function browserStorage(
  name: 'localStorage' | 'sessionStorage'
): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window[name];
  } catch {
    return null;
  }
}

export function getAccountFromStorage(
  storageName: 'sessionStorage' | 'localStorage',
  key: string,
  accountList: Account[]
): Account | null {
  const storage = browserStorage(storageName);
  if (storage == null) {
    return null;
  }
  try {
    const value = storage.getItem(key);
    return value == null ? null : safelyParseAccountJson(value, accountList);
  } catch {
    return null;
  }
}

export function writeAccountToStorage(account: Account): void {
  const value = JSON.stringify(account);
  const session = browserStorage('sessionStorage');
  if (session != null) {
    try {
      session.setItem(ACCOUNT_SELECTOR_VALUE, value);
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }
  const local = browserStorage('localStorage');
  if (local != null) {
    try {
      local.setItem(LAST_ACCOUNT_SELECTOR_VALUE, value);
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }
}

export function toShellAccount(
  isLoading: boolean,
  account: Account | null | undefined
): ShellAccount | null | undefined {
  if (isLoading) {
    return undefined;
  }
  if (account == null || account.id == null) {
    return null;
  }
  return {
    id: account.id,
    name: account.name,
    kind: account.accountType === 'Personal' ? 'personal' : 'organization',
    ...(account.isDummyAccount ? {isDummyAccount: true} : {}),
  };
}
